import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import {
  getInvoiceLinkRecipients,
  InvoiceLinkRecipient,
} from "../../../../services/api/paymentLinkService";
import { sendPaymentLink } from "../../../../redux/slices/invoicesSlice";
import { useAppDispatch } from "../../../../redux/store";

// Sending used to be one click to whichever address the customer record
// happened to hold. That is the wrong answer often enough to matter: an account
// with two people on it, or a record whose address is stale while the users who
// can see the customer in the portal have working ones. The dialog asks.
type Props = {
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  invoiceNumber?: string;
  onSent?: (result: { to?: string[]; failed?: string[] }) => void;
};

const SendPaymentLinkDialog = ({ open, onClose, invoiceId, invoiceNumber, onSent }: Props) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [recipients, setRecipients] = useState<InvoiceLinkRecipient[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  // The candidates are read when the dialog opens, so the list is never a
  // stale copy of who had access when the page was loaded.
  useEffect(() => {
    if (!open || !invoiceId) return;

    let cancelled = false;
    setLoading(true);
    setError("");
    setSelected([]);

    getInvoiceLinkRecipients(invoiceId)
      .then((data) => {
        if (cancelled) return;
        const list = data?.recipients || [];
        setRecipients(list);
        // The address the invoice itself carries is what the one-click send
        // would have used, so it starts selected.
        setSelected(list.filter((r) => r.onRecord && r.email).map((r) => r.email));
      })
      .catch((loadError: any) => {
        if (cancelled) return;
        // The API says why — an invoice the portal has no row for, a provider
        // that could not be read — and that sentence is more use than anything
        // this dialog could invent.
        setError(
          typeof loadError === "string" && loadError
            ? loadError
            : loadError?.message || "Could not load the recipients for this invoice.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, invoiceId]);

  const selectable = useMemo(() => recipients.filter((r) => !!r.email), [recipients]);
  const allSelected = selectable.length > 0 && selected.length === selectable.length;

  const toggle = (email: string) => {
    setSelected((current) =>
      current.includes(email) ? current.filter((e) => e !== email) : [...current, email],
    );
  };

  const handleSend = async () => {
    if (selected.length === 0) return;
    setSending(true);
    setError("");
    try {
      const result: any = await dispatch(
        sendPaymentLink({ invoiceId, recipients: selected }),
      ).unwrap();
      onSent?.({ to: result?.to, failed: result?.failed });
      onClose();
    } catch (sendError: any) {
      setError(
        typeof sendError === "string" && sendError
          ? sendError
          : sendError?.message || "Failed to send the payment link.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: '"Brandon Grotesque", sans-serif', fontSize: 16 }}>
        Send a payment link
      </DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, color: "rgba(29,29,28,0.7)", mb: 2 }}>
          {invoiceNumber ? `Invoice ${invoiceNumber}. ` : ""}
          Each person gets their own email, and nobody is copied on anyone else's link.
        </Typography>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        )}

        {!loading && error && (
          <Typography sx={{ fontSize: 13, color: "#dc2626" }}>{error}</Typography>
        )}

        {!loading && !error && (
          <>
            {recipients.length === 0 && (
              <Typography sx={{ fontSize: 13, color: "rgba(29,29,28,0.7)" }}>
                No address is on file for this client, and no portal user has access to them.
                Add an address to the customer, or give someone access to this project.
              </Typography>
            )}

            {recipients.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selected.length > 0 && !allSelected}
                    onChange={() =>
                      setSelected(allSelected ? [] : selectable.map((r) => r.email))
                    }
                  />
                }
                label={
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    Select all ({selectable.length})
                  </Typography>
                }
              />
            )}

            <Box sx={{ mt: 0.5 }}>
              {recipients.map((recipient) => {
                const hasEmail = !!recipient.email;
                return (
                  <FormControlLabel
                    key={recipient.id || recipient.email}
                    sx={{ display: "flex", alignItems: "flex-start", mb: 0.5 }}
                    control={
                      <Checkbox
                        size="small"
                        disabled={!hasEmail}
                        checked={hasEmail && selected.includes(recipient.email)}
                        onChange={() => hasEmail && toggle(recipient.email)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontSize: 13 }}>
                          {recipient.name || recipient.email}
                          {recipient.onRecord && (
                            <Typography component="span" sx={{ fontSize: 11, ml: 1, color: "rgba(29,29,28,0.55)" }}>
                              on the invoice
                            </Typography>
                          )}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "rgba(29,29,28,0.7)" }}>
                          {hasEmail ? recipient.email : "no email on file"}
                          {recipient.role ? ` · ${recipient.role}` : ""}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} size="small" disabled={sending}>
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          size="small"
          variant="contained"
          disabled={sending || selected.length === 0}
        >
          {sending ? "Sending…" : `Send link (${selected.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendPaymentLinkDialog;
